import { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Search, Folder, Briefcase, Edit2, Archive, RotateCcw, CheckCircle2,
  X, Calendar as CalIcon, ListChecks, Sparkles, Layers, AlertTriangle,
  PauseCircle, ArrowDownUp, Trash2, ChevronLeft, ChevronRight,
} from 'lucide-react';
import { projectsApi, Project, ProjectCreatePayload, ProjectUpdatePayload } from '../../api/projects';
import ConfirmDialog, { type ConfirmState } from '../../components/ConfirmDialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { DateField } from '../../components/ui/DateField';
import { clientsApi, Client } from '../../api/clients';
import { vendorsApi, Vendor } from '../../api/vendors';
import { getEmployees } from '../../api/employees';
import { useAuth } from '../../contexts/AuthContext';
import { useAccess } from '../../contexts/AccessContext';
import PersonAvatar from '../../components/people/PersonAvatar';
import { toast } from "sonner";

// Mirrors the Tasks tab so Projects paginates the same way the global
// Tasks list does — 9 cards per page (3×3 on lg).
const PROJECTS_PAGE_SIZE = 9;

interface EmployeeOption {
  id: string;
  name: string;
  email?: string | null;
}

type StatusKey = 'ALL' | 'ACTIVE' | 'ON_HOLD' | 'COMPLETED' | 'ARCHIVED';

const STATUS_OPTIONS = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'ON_HOLD', label: 'On Hold' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'ARCHIVED', label: 'Archived' },
] as const;

const STATUS_META: Record<string, { color: string; bg: string; label: string }> = {
  ACTIVE:    { color: '#16a34a', bg: 'rgba(22,163,74,0.10)',  label: 'Active' },
  ON_HOLD:   { color: '#d97706', bg: 'rgba(217,119,6,0.10)',  label: 'On Hold' },
  COMPLETED: { color: '#2563eb', bg: 'rgba(37,99,235,0.10)',  label: 'Completed' },
  ARCHIVED:  { color: '#6b7280', bg: 'rgba(107,114,128,0.10)', label: 'Archived' },
};

interface PickerOption {
  id: string;
  label: string;
  meta?: string | null;
}

interface FormState {
  name: string;
  description: string;
  code: string;
  client_ids: string[];
  vendor_ids: string[];
  member_ids: string[];
  status: 'ACTIVE' | 'ON_HOLD' | 'COMPLETED' | 'ARCHIVED';
  start_date: string;
  end_date: string;
}

const EMPTY_FORM: FormState = {
  name: '',
  description: '',
  code: '',
  client_ids: [],
  vendor_ids: [],
  member_ids: [],
  status: 'ACTIVE',
  start_date: '',
  end_date: '',
};

/**
 * Fold a project's two stored link shapes into one ordered selection.
 *
 * The backend keeps the singular FK as the primary link alongside the array;
 * a project saved before multi-select existed has only the singular one, so
 * reading both is what stops its client from vanishing out of the edit form.
 */
function linkedIds(ids: string[] | null | undefined, primary: string | null | undefined): string[] {
  const out: string[] = [];
  for (const id of [...(ids || []), primary]) {
    if (id && !out.includes(id)) out.push(id);
  }
  return out;
}

/**
 * Keep a link the directory has since lost as a pickable option.
 *
 * Deleting a client only nulls the singular FK — a JSON array can carry no FK,
 * so the id stays on the project. Without this the chip would read as a raw
 * UUID and the user could not tell what to remove.
 */
function withOrphans(
  base: PickerOption[],
  ids: string[] | undefined,
  names: (string | null)[] | undefined,
  fallback: string,
): PickerOption[] {
  const known = new Set(base.map(o => o.id));
  const extra = (ids || [])
    .map((id, i) => ({ id, label: names?.[i] || fallback }))
    .filter(o => !known.has(o.id));
  return extra.length ? [...base, ...extra] : base;
}

/**
 * Chip-strip multi-select — the modal's shape for every "pick several" field.
 *
 * Team Members grew this first; Client and Vendor moved onto it when they
 * stopped being single-pick, and sharing one component is what keeps the three
 * fields from drifting into three slightly different pickers. Open state is
 * owned by the modal so only one dropdown can be open at a time.
 */
function ChipMultiSelect({
  options,
  values,
  onChange,
  placeholder,
  searchPlaceholder,
  emptyText,
  open,
  onOpenChange,
  ariaLabel,
  people = false,
}: {
  options: PickerOption[];
  values: string[];
  onChange: (ids: string[]) => void;
  placeholder: string;
  searchPlaceholder: string;
  emptyText: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  ariaLabel: string;
  /** Options are people (Team Members) → show their profile photo. Clients and vendors are not people. */
  people?: boolean;
}) {
  const [query, setQuery] = useState('');

  // A stale query would silently hide most of the list the next time this
  // picker opens, so it resets on close rather than on open.
  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  const filtered = options.filter(o => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return `${o.label} ${o.meta || ''}`.toLowerCase().includes(q);
  });

  const toggle = (id: string) =>
    onChange(values.includes(id) ? values.filter(x => x !== id) : [...values, id]);

  return (
    <div className="relative">
      <div
        className="w-full min-h-[42px] px-3 py-2 border border-gray-300 rounded-lg bg-white focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500 flex flex-wrap gap-1.5 items-center cursor-pointer"
        onClick={() => onOpenChange(true)}
      >
        {values.length === 0 && (
          <span className="text-sm text-gray-400">{placeholder}</span>
        )}
        {values.map(id => {
          const label = options.find(o => o.id === id)?.label || id;
          return (
            <span
              key={id}
              className={`inline-flex items-center gap-1 ${people ? 'pl-0.5 pr-2' : 'px-2'} py-0.5 bg-blue-50 text-blue-700 text-xs font-medium rounded-full border border-blue-200`}
            >
              {people && <PersonAvatar userId={id} employeeId={id} name={label} size={20} />}
              {label}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onChange(values.filter(x => x !== id));
                }}
                className="text-blue-500 hover:text-blue-800"
                aria-label={`Remove ${label}`}
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          );
        })}
        <button
          type="button"
          aria-label={ariaLabel}
          onClick={(e) => { e.stopPropagation(); onOpenChange(!open); }}
          className="ml-auto text-xs font-medium text-blue-600 hover:text-blue-800"
        >
          {open ? 'Close' : '+ Add'}
        </button>
      </div>

      {open && (
        <div className="absolute z-20 mt-1 left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-hidden flex flex-col">
          <div className="p-2 border-b border-gray-100">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={searchPlaceholder}
              className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
              autoFocus
            />
          </div>
          <div className="overflow-y-auto max-h-48 py-1">
            {filtered.map(o => {
              const selected = values.includes(o.id);
              return (
                <button
                  key={o.id}
                  type="button"
                  onClick={() => toggle(o.id)}
                  className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 hover:bg-gray-50 ${selected ? 'bg-blue-50' : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={selected}
                    readOnly
                    className="h-4 w-4 text-blue-600 rounded pointer-events-none"
                  />
                  {people && <PersonAvatar userId={o.id} employeeId={o.id} name={o.label} size={24} />}
                  <span className="flex-1 truncate">
                    <span className="font-medium text-gray-900">{o.label}</span>
                    {o.meta && (
                      <span className="ml-2 text-xs text-gray-500">{o.meta}</span>
                    )}
                  </span>
                </button>
              );
            })}
            {filtered.length === 0 && (
              <div className="px-3 py-4 text-sm text-gray-500 text-center">
                {query ? `No matches for "${query}".` : emptyText}
              </div>
            )}
          </div>
          <div className="border-t border-gray-100 px-2 py-1.5 flex items-center justify-between">
            <span className="text-xs text-gray-500 pl-1">{values.length} selected</span>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="text-xs font-medium text-gray-600 hover:text-gray-900 px-2 py-1"
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function ProjectsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const currentUserId = user?.id || '';
  // Creating / editing a project = the "projects" panel at Manage in Access
  // Management; no role list.
  const { canManage } = useAccess();
  const canEdit = canManage('projects');
  const [projects, setProjects] = useState<Project[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [employees, setEmployees] = useState<EmployeeOption[]>([]);
  // UI-only: which of the modal's three multi-selects has its dropdown open.
  // A single value is what keeps two of them from overlapping each other.
  const [openPicker, setOpenPicker] = useState<'client' | 'vendor' | 'member' | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusKey>('ALL');
  const [clientFilter, setClientFilter] = useState<string>('');
  const [vendorFilter, setVendorFilter] = useState<string>('');
  const [createdByMe, setCreatedByMe] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<Project | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  // In-app confirmation dialog (replaces native window.confirm).
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);

  const loadProjects = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: any = { page_size: 200, include_archived: true };
      if (search) params.search = search;
      if (clientFilter) params.client_id = clientFilter;
      if (vendorFilter) params.vendor_id = vendorFilter;
      if (createdByMe && currentUserId) params.created_by = currentUserId;
      const r = await projectsApi.list(params);
      setProjects(r.data?.items || []);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to load projects');
    } finally {
      setLoading(false);
    }
  }, [search, clientFilter, vendorFilter, createdByMe, currentUserId]);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  useEffect(() => {
    clientsApi.list({ page_size: 200 })
      .then(r => setClients(r.data?.items || []))
      .catch(() => {});
    vendorsApi.list({ page_size: 200 })
      .then(r => setVendors(r.data?.items || []))
      .catch(() => {});
    getEmployees({ include_peers: true })
      .then((data: any) => {
        const list: any[] = Array.isArray(data) ? data : (data?.employees || data?.items || []);
        const mapped: EmployeeOption[] = list
          .map((e: any) => {
            const id = e.user_id || e.userId; // canonical users.id
            const first = (e.first_name || e.firstName || '').trim();
            const last = (e.last_name || e.lastName || '').trim();
            const name = e.name || `${first} ${last}`.trim() || e.email || id;
            return id ? { id, name, email: e.email || null } : null;
          })
          .filter((x: EmployeeOption | null): x is EmployeeOption => !!x);
        setEmployees(mapped);
      })
      .catch(() => {});
  }, []);

  const counts = useMemo(() => {
    const c: Record<string, number> = { ALL: 0, ACTIVE: 0, ON_HOLD: 0, COMPLETED: 0, ARCHIVED: 0 };
    projects.forEach(p => {
      c.ALL += 1;
      c[p.status] = (c[p.status] || 0) + 1;
    });
    return c;
  }, [projects]);

  const filtered = useMemo(() => {
    if (statusFilter === 'ALL') return projects;
    return projects.filter(p => p.status === statusFilter);
  }, [projects, statusFilter]);

  const totalTasks = useMemo(
    () => projects.reduce((acc, p) => acc + (p.task_counts?.total || 0), 0),
    [projects],
  );

  // Pagination — 9 cards per page (3×3 on lg), matching the Tasks tab.
  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PROJECTS_PAGE_SIZE));
  const visibleProjects = useMemo(
    () => filtered.slice(
      (currentPage - 1) * PROJECTS_PAGE_SIZE,
      currentPage * PROJECTS_PAGE_SIZE,
    ),
    [filtered, currentPage],
  );
  const rangeStart = filtered.length === 0 ? 0 : (currentPage - 1) * PROJECTS_PAGE_SIZE + 1;
  const rangeEnd = Math.min(currentPage * PROJECTS_PAGE_SIZE, filtered.length);

  // Snap back to page 1 whenever the filtered list shrinks past the
  // current page (e.g. switching status filter from "All" to "Archived").
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, search, clientFilter, vendorFilter, createdByMe]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setShowModal(true);
  };

  const openEdit = (p: Project) => {
    setEditing(p);
    setForm({
      name: p.name,
      description: p.description || '',
      code: p.code || '',
      client_ids: linkedIds(p.client_ids, p.client_id),
      vendor_ids: linkedIds(p.vendor_ids, p.vendor_id),
      member_ids: p.member_ids || [],
      status: p.status,
      start_date: p.start_date || '',
      end_date: p.end_date || '',
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditing(null);
    setForm(EMPTY_FORM);
    setOpenPicker(null);
  };

  // Options for the modal's three pickers.
  const clientOptions = useMemo<PickerOption[]>(
    () => withOrphans(
      clients.map(c => ({ id: c.id, label: c.clientName })),
      editing?.client_ids, editing?.client_names, 'Deleted client',
    ),
    [clients, editing],
  );

  const vendorOptions = useMemo<PickerOption[]>(
    () => withOrphans(
      vendors.map(v => ({ id: v.id, label: v.clientName })),
      editing?.vendor_ids, editing?.vendor_names, 'Deleted vendor',
    ),
    [vendors, editing],
  );

  // Members already on the project are resolved by the project payload, so a
  // member outside the loaded employee page still shows their name.
  const memberOptions = useMemo<PickerOption[]>(() => {
    const base = employees.map(e => ({ id: e.id, label: e.name, meta: e.email }));
    const known = new Set(base.map(o => o.id));
    const extra = (editing?.members || [])
      .filter(m => !known.has(m.id))
      .map(m => ({ id: m.id, label: m.name, meta: m.email }));
    return extra.length ? [...base, ...extra] : base;
  }, [employees, editing]);

  const handleSave = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const payload: ProjectCreatePayload | ProjectUpdatePayload = {
        name: form.name.trim(),
        description: form.description || undefined,
        code: form.code || undefined,
        // Both shapes travel: the arrays are the selection, the singular
        // fields name its first entry so the backend's primary link is the
        // same one the user sees listed first.
        client_id: form.client_ids[0] || null,
        client_ids: form.client_ids,
        vendor_id: form.vendor_ids[0] || null,
        vendor_ids: form.vendor_ids,
        member_ids: form.member_ids,
        status: form.status,
        start_date: form.start_date || undefined,
        end_date: form.end_date || undefined,
      };
      if (editing) {
        await projectsApi.update(editing.id, payload);
      } else {
        await projectsApi.create(payload as ProjectCreatePayload);
      }
      closeModal();
      await loadProjects();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Failed to save project');
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = (p: Project) => {
    setConfirm({
      title: 'Archive project?',
      description: <>Project <strong>"{p.name}"</strong> will be moved to Archived. You can reopen it later from the Archived filter.</>,
      confirmLabel: 'Archive',
      tone: 'default',
      onConfirm: async () => {
        try {
          await projectsApi.delete(p.id);
          await loadProjects();
        } catch (e: any) {
          toast.error(e?.response?.data?.detail || 'Failed to archive');
          throw e;
        }
      },
    });
  };

  const handleReopen = async (p: Project) => {
    try {
      await projectsApi.reopen(p.id);
      await loadProjects();
    } catch {
      toast.error('Failed to reopen');
    }
  };

  const handleComplete = (p: Project) => {
    setConfirm({
      title: 'Mark as completed?',
      description: <>Project <strong>"{p.name}"</strong> will be marked Completed.</>,
      confirmLabel: 'Mark Completed',
      tone: 'success',
      onConfirm: async () => {
        try {
          await projectsApi.complete(p.id);
          await loadProjects();
        } catch (e: any) {
          toast.error(e?.response?.data?.detail || 'Failed to complete');
          throw e;
        }
      },
    });
  };

  // Soft delete — moves the project to "Recently Deleted". From there it can
  // be restored or permanently removed.
  const handleDelete = (p: Project) => {
    setConfirm({
      title: 'Delete project?',
      description: <>Project <strong>"{p.name}"</strong> will be moved to Recently Deleted — you can restore it from there.</>,
      confirmLabel: 'Delete',
      tone: 'danger',
      onConfirm: async () => {
        try {
          await projectsApi.trash(p.id);
          await loadProjects();
        } catch (e: any) {
          toast.error(e?.response?.data?.detail || 'Failed to delete');
          throw e;
        }
      },
    });
  };

  const statusTiles: Array<{ key: StatusKey; label: string; tone: string; icon: any }> = [
    { key: 'ALL',       label: 'All',       tone: 'all',       icon: Layers },
    { key: 'ACTIVE',    label: 'Active',    tone: 'active',    icon: Sparkles },
    { key: 'ON_HOLD',   label: 'On Hold',   tone: 'hold',      icon: PauseCircle },
    { key: 'COMPLETED', label: 'Completed', tone: 'completed', icon: CheckCircle2 },
    { key: 'ARCHIVED',  label: 'Archived',  tone: 'archived',  icon: Archive },
  ];

  const tileStyle = (tone: string, active: boolean): React.CSSProperties => {
    const palette: Record<string, { bg: string; fg: string; border: string }> = {
      all:       { bg: 'var(--gt-overlay-green500-10)',  fg: 'var(--gt-green-700)', border: 'var(--gt-overlay-green500-25)' },
      active:    { bg: 'var(--gt-overlay-green500-12)',  fg: 'var(--gt-green-800)', border: 'var(--gt-overlay-green500-30)' },
      hold:      { bg: 'var(--gt-overlay-yellow-12)',  fg: 'var(--gt-amber-900)', border: 'var(--gt-overlay-yellow-30)' },
      completed: { bg: 'var(--gt-overlay-blue-12)', fg: 'var(--gt-blue-800)', border: 'var(--gt-overlay-blue-30)' },
      archived:  { bg: 'var(--gt-overlay-gray-10)',fg: 'var(--gt-gray-700)', border: 'var(--gt-overlay-gray-25)' },
    };
    const p = palette[tone];
    return {
      background: active ? p.fg : p.bg,
      color: active ? 'var(--gt-white)' : p.fg,
      border: `1px solid ${active ? p.fg : p.border}`,
      borderRadius: 12,
      padding: '14px 16px',
      cursor: 'pointer',
      transition: 'all 0.2s',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 8,
      boxShadow: active ? '0 4px 12px -4px rgba(0,0,0,0.12)' : 'none',
    };
  };

  if (loading) {
    return (
      <div className="gt-shell flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="gt-loading-ring mx-auto mb-3" />
          <p className="gt-page-sub gt-serif-italic">Loading <em>projects</em>…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="gt-shell flex items-center justify-center min-h-[60vh]">
        <div className="gt-card p-8 max-w-md text-center" style={{ borderColor: 'rgba(220,38,38,0.18)' }}>
          <AlertTriangle className="w-9 h-9 mx-auto mb-3" style={{ color: 'var(--gt-dnd)' }} />
          <p className="gt-display" style={{ fontSize: 14, color: 'var(--gt-text-primary)' }}>{error}</p>
          <button onClick={loadProjects} className="gt-btn is-danger mt-4 mx-auto">Try Again</button>
        </div>
      </div>
    );
  }

  return (
    <div className="gt-shell min-h-screen">
      <div className="max-w-7xl mx-auto p-4 md:p-7 space-y-6 gt-fade-in">
        {/* Header */}
        <div className="gt-page-hero flex-col md:flex-row gap-4">
          <div>
            <h1 className="gt-page-title">Project <em>Management</em></h1>
            <p className="gt-page-sub" style={{ marginTop: 6 }}>
              Group and track your <em className="gt-serif-italic">organizational</em> projects.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {canEdit && (
              <button onClick={openCreate} className="gt-btn is-primary is-lg">
                <Plus className="w-4 h-4" />
                New Project
              </button>
            )}
            {/* Recently Deleted — icon-only square on mobile, full label on desktop */}
            <button
              onClick={() => navigate('/projects/recently-deleted')}
              className="gt-btn is-danger gt-project-trash-btn"
              aria-label="Recently Deleted"
              title="Recently Deleted"
            >
              <Trash2 className="w-4 h-4" />
              <span className="hidden md:inline">Recently Deleted</span>
            </button>
          </div>
        </div>

        {/* Stat Cards — hidden on mobile to keep the list closer to the top */}
        {projects.length > 0 && (
          <div className="hidden md:grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="gt-stat">
              <div className="gt-stat-label"><Folder className="w-3.5 h-3.5" /> Total Projects</div>
              <p className="gt-stat-value">{counts.ALL}</p>
            </div>
            <div className="gt-stat">
              <div className="gt-stat-label"><Sparkles className="w-3.5 h-3.5" /> Active</div>
              <p className="gt-stat-value" style={{ color: 'var(--gt-accent-strong)' }}>{counts.ACTIVE || 0}</p>
            </div>
            <div className="gt-stat">
              <div className="gt-stat-label"><ListChecks className="w-3.5 h-3.5" /> Linked Tasks</div>
              <p className="gt-stat-value">{totalTasks}</p>
            </div>
          </div>
        )}

        {/* Status Tile Filter — 2 cols up to md (matches the mobile mockup), 5 cols on desktop.
            Forced via inline style so no parent CSS can collapse it to a single column. */}
        <div
          className="gt-project-status-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
            gap: 8,
          }}
        >
          {statusTiles.map(t => {
            const Icon = t.icon;
            const isActive = statusFilter === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setStatusFilter(t.key)}
                style={tileStyle(t.tone, isActive)}
                className="gt-project-status-tile"
              >
                <span className="flex items-center gap-2">
                  <Icon className="w-3.5 h-3.5 gt-project-status-tile__icon" />
                  <span className="text-xs uppercase tracking-wide font-semibold gt-project-status-tile__label">{t.label}</span>
                </span>
                <span className="text-lg font-bold gt-numerals gt-project-status-tile__count">{counts[t.key] || 0}</span>
              </button>
            );
          })}
        </div>

        {/* Filters */}
        <div className="gt-card overflow-hidden">
          <button
            onClick={() => setShowFilters(s => !s)}
            className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
          >
            <span className="flex items-center gap-2 gt-display" style={{ fontSize: 14, color: 'var(--gt-text-primary)' }}>
              <Search className="w-4 h-4" /> Filters
            </span>
            <span className="gt-page-sub text-xs">{showFilters ? 'Hide' : 'Show'}</span>
          </button>
          {showFilters && (
            <div className="px-4 pb-4 flex flex-col md:flex-row gap-3 md:items-center border-t border-gray-100 pt-4">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search projects..."
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                />
              </div>
              <div className="min-w-[180px]">
                <Select
                  value={clientFilter || "__all__"}
                  onValueChange={(v) => setClientFilter(v === "__all__" ? "" : v)}
                >
                  <SelectTrigger><SelectValue placeholder="All Clients" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All Clients</SelectItem>
                    {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.clientName}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="min-w-[180px]">
                <Select
                  value={vendorFilter || "__all__"}
                  onValueChange={(v) => setVendorFilter(v === "__all__" ? "" : v)}
                >
                  <SelectTrigger><SelectValue placeholder="All Vendors" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All Vendors</SelectItem>
                    {vendors.map(v => <SelectItem key={v.id} value={v.id}>{v.clientName}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <label
                className="flex items-center gap-2 cursor-pointer select-none"
                title="Only show projects I created"
              >
                <input
                  type="checkbox"
                  checked={createdByMe}
                  onChange={(e) => setCreatedByMe(e.target.checked)}
                  disabled={!currentUserId}
                  style={{ accentColor: 'var(--gt-primary)' }}
                />
                <span className="gt-page-sub" style={{ fontSize: 13 }}>Created by me</span>
              </label>
            </div>
          )}
        </div>

        {/* Body */}
        {filtered.length === 0 ? (
          <div className="gt-card p-12 text-center">
            <div className="gt-empty">
              <div className="gt-empty-leaf"><Folder className="w-6 h-6" /></div>
              <div className="gt-empty-title">
                {projects.length === 0 ? 'No projects yet' : 'No projects in this view'}
              </div>
              <div className="gt-page-sub">
                {projects.length === 0
                  ? <>A clean slate. <em className="gt-serif-italic">Begin</em> with your first project.</>
                  : 'Try a different status filter.'}
              </div>
              {projects.length === 0 && canEdit && (
                <button onClick={openCreate} className="gt-btn is-primary mt-5 mx-auto">
                  <Plus className="w-4 h-4" /> Create your first project
                </button>
              )}
            </div>
          </div>
        ) : (
          <>
            <div className="flex justify-between items-center">
              <p className="gt-page-sub gt-numerals">
                Showing{' '}
                <span className="gt-display" style={{ color: 'var(--gt-text-primary)' }}>
                  {rangeStart}–{rangeEnd}
                </span>{' '}
                of{' '}
                <span className="gt-display" style={{ color: 'var(--gt-text-primary)' }}>{filtered.length}</span>{' '}
                {filtered.length !== counts.ALL && (
                  <>
                    (filtered from{' '}
                    <span className="gt-display" style={{ color: 'var(--gt-text-primary)' }}>{counts.ALL}</span>){' '}
                  </>
                )}
                projects
              </p>
              <div className="flex items-center gap-2 gt-page-sub" style={{ background: 'var(--gt-surface-2)', padding: '6px 12px', borderRadius: 999 }}>
                <ArrowDownUp className="w-3.5 h-3.5" />
                <span className="gt-eyebrow" style={{ marginBottom: 0 }}>Newest first</span>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {visibleProjects.map(p => <ProjectCard
                key={p.id}
                project={p}
                canEdit={canEdit}
                onView={() => navigate(`/projects/${p.id}`)}
                onEdit={() => openEdit(p)}
                onArchive={() => handleArchive(p)}
                onDelete={() => handleDelete(p)}
                onReopen={() => handleReopen(p)}
                onComplete={() => handleComplete(p)}
              />)}
            </div>

            {totalPages > 1 && (
              <>
                <div className="mt-8 flex justify-center items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="gt-btn is-ghost is-sm"
                  >
                    <ChevronLeft className="w-4 h-4" /> Previous
                  </button>

                  <div className="flex items-center gap-1.5">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) pageNum = i + 1;
                      else if (currentPage <= 3) pageNum = i + 1;
                      else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                      else pageNum = currentPage - 2 + i;

                      return (
                        <button
                          key={pageNum}
                          onClick={() => setCurrentPage(pageNum)}
                          className={currentPage === pageNum ? 'gt-btn is-primary is-sm' : 'gt-btn is-ghost is-sm'}
                          style={{ width: 36, height: 36, justifyContent: 'center', padding: 0 }}
                        >
                          <span className="gt-numerals">{pageNum}</span>
                        </button>
                      );
                    })}
                  </div>

                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="gt-btn is-ghost is-sm"
                  >
                    Next <ChevronRight className="w-4 h-4" />
                  </button>
                </div>

                <div className="text-center gt-page-sub gt-numerals md:hidden">
                  Page {currentPage} of {totalPages}
                </div>
              </>
            )}
          </>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div
          className="fixed inset-0 z-50 flex items-start sm:items-center justify-center p-3 overflow-y-auto"
          style={{ background: 'rgba(15, 29, 20, 0.45)', backdropFilter: 'blur(6px)' }}
          onClick={closeModal}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl my-6"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-3 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <span className="gt-leaf" />
                <h3 className="gt-display" style={{ fontSize: 22, color: 'var(--gt-text-primary)' }}>
                  {editing ? 'Edit Project' : 'Create Project'}
                </h3>
              </div>
              <button onClick={closeModal} className="p-1.5 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-6 py-4 space-y-3">
              {/* Row 1: Project Name + Code */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                    Project Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    placeholder="e.g. Website Redesign"
                    autoFocus
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Code</label>
                  <input
                    type="text"
                    value={form.code}
                    onChange={e => setForm(f => ({ ...f, code: e.target.value }))}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    placeholder="WEB-2026"
                  />
                </div>
              </div>

              {/* Row 2: Status + Client + Vendor */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Status</label>
                  <Select
                    value={form.status}
                    onValueChange={(v) => setForm(f => ({ ...f, status: v as FormState['status'] }))}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {/* Clients — multi-select, same chip strip as Team Members.
                    The first chip is the primary link the backend keeps on the
                    singular column for reports and the delete guard. */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                    Clients <span className="text-gray-400 font-normal">(optional)</span>
                  </label>
                  <ChipMultiSelect
                    options={clientOptions}
                    values={form.client_ids}
                    onChange={(ids) => setForm(f => ({ ...f, client_ids: ids }))}
                    placeholder="Select clients..."
                    searchPlaceholder="Search client..."
                    emptyText="No clients found."
                    ariaLabel="Add clients"
                    open={openPicker === 'client'}
                    onOpenChange={(o) => setOpenPicker(o ? 'client' : null)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                    Vendors <span className="text-gray-400 font-normal">(optional)</span>
                  </label>
                  <ChipMultiSelect
                    options={vendorOptions}
                    values={form.vendor_ids}
                    onChange={(ids) => setForm(f => ({ ...f, vendor_ids: ids }))}
                    placeholder="Select vendors..."
                    searchPlaceholder="Search vendor..."
                    emptyText="No vendors found."
                    ariaLabel="Add vendors"
                    open={openPicker === 'vendor'}
                    onOpenChange={(o) => setOpenPicker(o ? 'vendor' : null)}
                  />
                </div>
              </div>

              {/* Row 3: Team Members + Start Date + End Date */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {/* Team Members — same picker as Clients and Vendors above. */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                    Team Members <span className="text-gray-400 font-normal">(optional)</span>
                  </label>
                  <ChipMultiSelect
                    options={memberOptions}
                    values={form.member_ids}
                    onChange={(ids) => setForm(f => ({ ...f, member_ids: ids }))}
                    placeholder="Select team members..."
                    searchPlaceholder="Search employee..."
                    emptyText="No employees found."
                    ariaLabel="Add team members"
                    people
                    open={openPicker === 'member'}
                    onOpenChange={(o) => setOpenPicker(o ? 'member' : null)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Start Date</label>
                  <DateField
                    value={form.start_date}
                    onChange={(v) => setForm(f => ({ ...f, start_date: v }))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">End Date</label>
                  <DateField
                    value={form.end_date}
                    onChange={(v) => setForm(f => ({ ...f, end_date: v }))}
                  />
                </div>
              </div>

              {/* Row 4: Description */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Description</label>
                <textarea
                  value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  rows={2}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  placeholder="Brief description..."
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 px-6 py-4 border-t border-gray-100 bg-gray-50 rounded-b-2xl">
              <button
                onClick={closeModal}
                disabled={saving}
                className="gt-btn is-ghost"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !form.name.trim()}
                className="gt-btn is-primary"
              >
                {saving ? 'Saving...' : editing ? 'Update Project' : 'Create Project'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* In-app confirmation dialog (replaces native window.confirm). */}
      <ConfirmDialog state={confirm} onClose={() => setConfirm(null)} />
    </div>
  );
}

interface ProjectCardProps {
  project: Project;
  // false for supervisor / employee — hides every mutating action on the card.
  canEdit: boolean;
  onView: () => void;
  onEdit: () => void;
  onArchive: () => void;
  onDelete: () => void;
  onReopen: () => void;
  onComplete: () => void;
}

function ProjectCard({ project: p, canEdit, onView, onEdit, onArchive, onDelete, onReopen, onComplete }: ProjectCardProps) {
  const counts = p.task_counts || {};
  const meta = STATUS_META[p.status] || STATUS_META.ACTIVE;
  const total = counts.total || 0;
  const open = counts.open || 0;
  const completed = total - open;
  const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
  const isClosed = p.status === 'ARCHIVED' || p.status === 'COMPLETED';

  return (
    <div
      className="gt-card p-5 flex flex-col cursor-pointer transition-all hover:-translate-y-0.5"
      style={{
        opacity: p.status === 'ARCHIVED' ? 0.75 : 1,
        borderLeft: `3px solid ${meta.color}`,
      }}
      onClick={onView}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex-1 min-w-0">
          <h3
            className="gt-display truncate"
            style={{ fontSize: 15, color: 'var(--gt-text-primary)', fontWeight: 600 }}
          >
            {p.name}
          </h3>
          {p.code && (
            <p className="gt-page-sub gt-numerals" style={{ fontSize: 11, marginTop: 2 }}>
              {p.code}
            </p>
          )}
        </div>
        <span
          className="text-[10px] uppercase tracking-wide px-2 py-1 rounded-full font-bold whitespace-nowrap"
          style={{ background: meta.bg, color: meta.color }}
        >
          {meta.label}
        </span>
      </div>

      {/* Description */}
      {p.description && (
        <p className="gt-page-sub line-clamp-2 mb-3" style={{ fontSize: 13 }}>
          {p.description}
        </p>
      )}

      {/* Meta */}
      <div className="space-y-1.5 mb-3">
        {/* Every linked client, then every linked vendor. Names come back
            index-aligned with the ids; an id the directory has lost resolves
            to null and is skipped rather than shown as a UUID. */}
        <div className="flex flex-wrap gap-1.5">
          {(p.client_ids || [])
            .map((id, i) => ({ id, name: p.client_names?.[i] }))
            .filter(x => x.name)
            .map(({ id, name }) => (
            <span
              key={`c-${id}`}
              className="inline-flex items-center gap-1.5"
              style={{
                padding: '3px 9px',
                background: 'var(--gt-primary-container)',
                color: 'var(--gt-primary-deep)',
                borderRadius: 6,
                border: '1px solid color-mix(in srgb, var(--gt-primary) 25%, transparent)',
                fontFamily: 'var(--gt-font-display)',
                fontWeight: 600,
                fontSize: 10.5,
                maxWidth: '100%',
              }}
              title="Client"
            >
              <Briefcase className="w-3 h-3" />
              <span className="truncate">{name}</span>
            </span>
          ))}
          {(p.vendor_ids || [])
            .map((id, i) => ({ id, name: p.vendor_names?.[i] }))
            .filter(x => x.name)
            .map(({ id, name }) => (
            <span
              key={`v-${id}`}
              className="inline-flex items-center gap-1.5"
              style={{
                padding: '3px 9px',
                background: 'var(--gt-accent-container, #fef3c7)',
                color: 'var(--gt-accent-deep, #92400e)',
                borderRadius: 6,
                border: '1px solid color-mix(in srgb, #f59e0b 25%, transparent)',
                fontFamily: 'var(--gt-font-display)',
                fontWeight: 600,
                fontSize: 10.5,
                maxWidth: '100%',
              }}
              title="Vendor"
            >
              <Briefcase className="w-3 h-3" />
              <span className="truncate">{name}</span>
            </span>
          ))}
        </div>
        {(p.start_date || p.end_date) && (
          <div className="flex items-center gap-1.5 gt-page-sub gt-numerals" style={{ fontSize: 11 }}>
            <CalIcon className="w-3 h-3" />
            <span>{p.start_date || '...'} → {p.end_date || '...'}</span>
          </div>
        )}
      </div>

      {/* Progress */}
      <div className="mt-auto">
        <div className="flex items-center justify-between mb-1.5 text-xs">
          <span className="gt-page-sub flex items-center gap-1">
            <ListChecks className="w-3 h-3" />
            <span className="gt-numerals">{total} tasks</span>
            {open > 0 && <span className="gt-numerals">· {open} open</span>}
          </span>
          <span className="gt-numerals gt-display" style={{ color: 'var(--gt-text-primary)', fontWeight: 600 }}>
            {pct}%
          </span>
        </div>
        <div
          className="h-1.5 rounded-full overflow-hidden"
          style={{ background: 'var(--gt-surface-2)' }}
        >
          <div
            className="h-full transition-all"
            style={{ width: `${pct}%`, background: meta.color }}
          />
        </div>

        <div className="flex items-center gap-2 pt-3 mt-3 border-t border-gray-100">
          <button
            onClick={(e) => { e.stopPropagation(); onView(); }}
            className="flex-1 text-xs font-semibold text-blue-600 hover:text-blue-700 text-left"
          >
            View Tasks →
          </button>
          {isClosed ? (
            canEdit && (
              <button
                onClick={(e) => { e.stopPropagation(); onReopen(); }}
                className="p-1.5 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded transition-colors"
                title="Reopen"
              >
                <RotateCcw className="w-3.5 h-3.5" />
              </button>
            )
          ) : canEdit ? (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); onComplete(); }}
                className="p-1.5 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded transition-colors"
                title="Mark as Completed"
              >
                <CheckCircle2 className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); onEdit(); }}
                className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                title="Edit"
              >
                <Edit2 className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); onArchive(); }}
                className="p-1.5 text-gray-500 hover:text-amber-600 hover:bg-amber-50 rounded transition-colors"
                title="Archive"
              >
                <Archive className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); onDelete(); }}
                className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                title="Delete (moves to Recently Deleted)"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </>
          ) : (
            null
          )}
        </div>
      </div>
    </div>
  );
}
